import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import type { ReactNode } from 'react';
import { osirisTokens } from '@/theme/tokens';

const PURPLE_FILL = '#6e44f5';
const PURPLE_HOVER = '#826cfc';
const ERROR_FILL = '#dc543f';
const ERROR_HOVER = '#e3674f';
const CANCEL_BORDER = 'rgba(255, 255, 255, 0.48)';

export interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  /** Invoked when the confirm button is pressed. The modal does not close itself. */
  onConfirm: () => void;
  title: string;
  message: ReactNode;
  /** Optional secondary line below the main message (e.g. install instructions). */
  hint?: ReactNode;
  okText?: ReactNode;
  cancelText?: ReactNode;
  /** Confirm button style. 'destructive' = red, 'primary' (default) = purple. */
  tone?: 'primary' | 'destructive';
  /** Optional icon shown on the left of the modal header (e.g. a trash icon). */
  headerIcon?: ReactNode;
  confirmDisabled?: boolean;
  cancelDisabled?: boolean;
  className?: string;
}

export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  message,
  hint,
  okText,
  cancelText = 'Cancel',
  tone = 'primary',
  headerIcon,
  confirmDisabled,
  cancelDisabled,
  className,
}: ConfirmModalProps) {
  const isDestructive = tone === 'destructive';
  const confirmBg = isDestructive ? ERROR_FILL : PURPLE_FILL;
  const confirmHover = isDestructive ? ERROR_HOVER : PURPLE_HOVER;
  const defaultOkText = isDestructive ? 'Delete' : 'Confirm';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          className: `confirm-modal${className ? ' ' + className : ''}`,
          sx: {
            width: 500,
            maxWidth: 'calc(100vw - 32px)',
          },
        },
      }}
    >
      {/* Header: optional left icon, title, close X */}
      <Box
        className="confirm-modal-header"
        sx={{ height: 40, display: 'flex', alignItems: 'stretch', gap: '1px' }}
      >
        {headerIcon && (
          <Box
            className="confirm-modal-icon-l"
            sx={{
              width: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: osirisTokens.bg.header,
              color: osirisTokens.icons.primary,
            }}
          >
            {headerIcon}
          </Box>
        )}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            px: 2,
            bgcolor: osirisTokens.bg.header,
          }}
        >
          <Typography
            className="confirm-modal-title"
            sx={{
              fontFamily: osirisTokens.fonts.display,
              fontSize: 13,
              fontWeight: 400,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: osirisTokens.text.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          className="confirm-modal-close"
          sx={{
            width: 40,
            height: 40,
            borderRadius: 0,
            bgcolor: osirisTokens.bg.header,
            color: osirisTokens.icons.primary,
            '&:hover': { bgcolor: osirisTokens.alpha.dark150 },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box
        className="confirm-modal-content"
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, px: 5, pt: 2, pb: 3 }}
      >
        <Typography
          component="div"
          className="confirm-modal-message"
          sx={{
            width: '100%',
            fontFamily: osirisTokens.fonts.mono,
            fontSize: 13,
            fontWeight: 400,
            lineHeight: '20px',
            letterSpacing: '0.02em',
            color: osirisTokens.text.secondary,
            whiteSpace: 'pre-wrap',
            // long unbroken names (drone/app ids) must wrap, not overflow
            overflowWrap: 'anywhere',
          }}
        >
          {message}
        </Typography>
        {hint && (
          <Typography
            component="div"
            className="confirm-modal-hint"
            sx={{
              width: '100%',
              fontFamily: osirisTokens.fonts.body,
              fontSize: 12,
              fontWeight: 400,
              letterSpacing: '0.04em',
              color: osirisTokens.text.tertiary,
              overflowWrap: 'anywhere',
            }}
          >
            {hint}
          </Typography>
        )}
      </Box>

      {/* Actions */}
      <Box
        className="confirm-modal-actions"
        sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, px: 2, pb: 2 }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={onClose}
          disabled={cancelDisabled}
          className="confirm-modal-cancel"
          sx={{
            height: 32,
            px: 2,
            borderRadius: '2px',
            borderColor: CANCEL_BORDER,
            color: osirisTokens.text.secondary,
            fontFamily: osirisTokens.fonts.body,
            fontSize: 13,
            fontWeight: 500,
            textTransform: 'capitalize',
            letterSpacing: '0.005em',
            '&:hover': { borderColor: osirisTokens.text.primary, bgcolor: 'rgba(255,255,255,0.05)' },
          }}
        >
          {cancelText}
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={onConfirm}
          autoFocus
          disabled={confirmDisabled}
          className="confirm-modal-confirm"
          sx={{
            height: 32,
            px: 2,
            borderRadius: '2px',
            bgcolor: confirmBg,
            color: osirisTokens.text.primary,
            fontFamily: osirisTokens.fonts.body,
            fontSize: 13,
            fontWeight: 500,
            textTransform: 'capitalize',
            letterSpacing: '0.005em',
            boxShadow: 'none',
            '&:hover': { bgcolor: confirmHover, boxShadow: 'none' },
            '&.Mui-disabled': { bgcolor: confirmBg, color: osirisTokens.text.primary, opacity: 0.5 },
          }}
        >
          {okText ?? defaultOkText}
        </Button>
      </Box>
    </Dialog>
  );
}
